import { useMemo } from "react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { useDevices } from "../providers/DevicesProvider";

/**
 * Hook that provides browser compatibility information for video features
 * Uses AWS Chime SDK's built-in browser detection and device availability
 */
export function useBrowserCompatibilityFeatures() {
  const { audioInputDevices, videoInputDevices, isDevicesLoading } =
    useDevices();

  return useMemo(() => {
    const behavior = new DefaultBrowserBehavior();

    // Check if devices are available
    const hasAudioDevices = audioInputDevices.length > 0;
    const hasVideoDevices = videoInputDevices.length > 0;

    // Basic compatibility
    const isSupported = behavior.isSupported();

    // Check if user is on mobile
    const userAgent = typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : 'unknown';
    const isMobile = /mobi|android|iphone|ipad|ipod|blackberry|iemobile|opera mini|touch/i.test(userAgent);
    const browserName = behavior.name();

    // Feature support - combines browser support AND device availability
    const supportsAudio = behavior.isSupported() && hasAudioDevices;
    const supportsVideo = behavior.isSupported() && hasVideoDevices;
    
    // Screen sharing support: Enable for all non-mobile browsers
    const supportsScreenShare = !isMobile && behavior.isSupported() && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

    // Debug screen share support

    const supportsSimulcast = behavior.isSimulcastSupported();
    const supportsBackgroundFilter = behavior.supportsBackgroundFilter();
    const supportsAudioOutput = behavior.supportsSetSinkId();
    const supportsVideoFx = behavior.isVideoFxSupportedBrowser();

    // Browser info
    const browserVersion = behavior.version();
    const supportString = behavior.supportString();

    // A user is considered supported if browser and audio are supported and available
    const isUserSupported = isSupported && supportsAudio && !isDevicesLoading;

    const supportObject = {
      isDevicesLoading,
      isUserSupported,
      isSupported,
      supportsAudio,
      supportsVideo,
      supportsScreenShare,
      supportsSimulcast,
      supportsBackgroundFilter,
      supportsAudioOutput,
      supportsVideoFx,
      hasAudioDevices,
      hasVideoDevices,
      browserName,
      browserVersion,
      supportString,
    };

    return supportObject;
  }, [audioInputDevices.length, videoInputDevices.length]);
}
